/**
 * @fileoverview Shared ownership stack for TetherSync push toasts.
 * @module local-db/components/tetherSyncToastOwner
 *
 * Responsibilities:
 * - Track which mounted TetherSync widget currently owns push-toast rendering.
 * - Notify subscribed widgets when ownership changes.
 *
 * Dependencies:
 * - React useSyncExternalStore.
 *
 * Architecture:
 * - Module-scoped in-memory stack shared by every TetherSync widget in this package.
 *
 * Notes:
 * - Claims are released by their owning widget on cleanup; removing a newer claim
 *   restores the preceding owner.
 */
import { useSyncExternalStore } from 'react';

interface ToastOwnerClaim {
  id: string;
}

type Listener = () => void;

const claims: ToastOwnerClaim[] = [];
const listeners = new Set<Listener>();

function currentOwnerId(): string | undefined {
  return claims[claims.length - 1]?.id;
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notifyIfOwnerChanged(previousOwnerId: string | undefined): void {
  if (previousOwnerId === currentOwnerId()) return;
  listeners.forEach((listener) => listener());
}

/**
 * @process claimPushToastOwner
 * Steps: Push the widget ID onto the ownership stack and return a release function.
 * Inputs: Unique widget ID.
 * Outputs: Idempotent function that releases this claim.
 * Failure Handling: Releasing an already released or missing claim has no effect.
 * Idempotency: REQUIRED — release may be called more than once during cleanup.
 */
export function claimPushToastOwner(id: string): () => void {
  const claim = { id };
  const previousOwnerId = currentOwnerId();
  claims.push(claim);
  notifyIfOwnerChanged(previousOwnerId);

  let released = false;
  return () => {
    if (released) return;
    released = true;

    const claimIndex = claims.indexOf(claim);
    if (claimIndex === -1) return;

    const previousOwner = currentOwnerId();
    claims.splice(claimIndex, 1);
    notifyIfOwnerChanged(previousOwner);
  };
}

/**
 * @hook useIsPushToastOwner
 * Purpose: Subscribe to whether this widget is the newest push-toast owner.
 * Returns: True only while the supplied ID is at the top of the ownership stack.
 * Side Effects: Subscribes to the module-level ownership store and cleans up on unmount.
 * Offline Behavior: Ownership is local in-memory UI state and is independent of connectivity.
 */
export function useIsPushToastOwner(id: string): boolean {
  return useSyncExternalStore(
    subscribe,
    () => currentOwnerId() === id,
    () => false,
  );
}
